'use client'

import { useState, useCallback, useEffect, useRef } from 'react'

interface VocabItem {
  es: string
  pt: string
  tip: string
}

interface Props {
  contentPt: string
  contentEs: string
  vocab: VocabItem[]
}

type Mode = 'bilingual' | 'parallel' | 'spanish' | 'portuguese'

interface ActiveTip {
  item: VocabItem
  rect: { left: number; top: number; bottom: number; width: number }
}

function getParagraphs(html: string): string[] {
  const matches = html.match(/<p[^>]*>[\s\S]*?<\/p>/gi) || []
  return matches.map((p) => p.replace(/<[^>]*>/g, '').trim()).filter(Boolean)
}

/* ---------- Rótulo de idioma (substitui bandeiras emoji) ---------- */
function LangTag({ code }: { code: 'PT' | 'ES' }) {
  const isEs = code === 'ES'
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        padding: '2px 7px',
        borderRadius: 'var(--r-xs, 6px)',
        color: isEs ? 'var(--brand-primary)' : 'var(--fg3)',
        background: isEs
          ? 'color-mix(in oklab, var(--brand-primary) 12%, transparent)'
          : 'var(--bg-alt)',
        border: isEs
          ? '1px solid color-mix(in oklab, var(--brand-primary) 22%, transparent)'
          : '1px solid var(--border)',
        whiteSpace: 'nowrap',
      }}
    >
      {code}
    </span>
  )
}

/* ---------- Texto ES com vocabulário clicável ---------- */
function HighlightedText({
  text,
  vocab,
  activeEs,
  onTip,
}: {
  text: string
  vocab: VocabItem[]
  activeEs: string | null
  onTip: (item: VocabItem, el: HTMLElement) => void
}) {
  if (!vocab.length) return <>{text}</>
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0
  // ordena por posição de ocorrência para preservar a leitura
  while (remaining.length) {
    let bestIdx = -1
    let bestItem: VocabItem | null = null
    for (const item of vocab) {
      const idx = remaining.toLowerCase().indexOf(item.es.toLowerCase())
      if (idx !== -1 && (bestIdx === -1 || idx < bestIdx)) {
        bestIdx = idx
        bestItem = item
      }
    }
    if (bestIdx === -1 || !bestItem) {
      parts.push(<span key={key++}>{remaining}</span>)
      break
    }
    if (bestIdx > 0) parts.push(<span key={key++}>{remaining.slice(0, bestIdx)}</span>)
    const matched = remaining.slice(bestIdx, bestIdx + bestItem.es.length)
    const isActive = activeEs === bestItem.es
    const itemRef = bestItem
    parts.push(
      <button
        key={key++}
        onClick={(e) => onTip(itemRef, e.currentTarget)}
        style={{
          font: 'inherit',
          cursor: 'pointer',
          border: 'none',
          background: isActive
            ? 'color-mix(in oklab, var(--brand-primary) 14%, transparent)'
            : 'transparent',
          color: 'var(--brand-primary)',
          borderBottom: '1.5px dashed color-mix(in oklab, var(--brand-primary) 55%, transparent)',
          borderRadius: isActive ? '4px' : 0,
          padding: isActive ? '0 3px' : '0 1px',
          transition: 'background .15s ease',
        }}
      >
        {matched}
      </button>
    )
    remaining = remaining.slice(bestIdx + bestItem.es.length)
  }
  return <>{parts}</>
}

/* ---------- Tooltip: bottom-sheet no mobile, popover no desktop ---------- */
function VocabTip({
  tip,
  isMobile,
  onClose,
}: {
  tip: ActiveTip
  isMobile: boolean
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onScroll = () => { if (!isMobile) onClose() }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', onScroll)
    }
  }, [onClose, isMobile])

  const closeBtn = (
    <button
      onClick={onClose}
      aria-label="Fechar"
      style={{
        flexShrink: 0,
        width: isMobile ? 40 : 28,
        height: isMobile ? 40 : 28,
        borderRadius: 'var(--r-sm, 10px)',
        border: 'none',
        background: 'var(--bg-alt)',
        color: 'var(--fg3)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </button>
  )

  const inner = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <LangTag code="ES" />
          <span
            style={{
              fontFamily: 'var(--font-editorial, var(--font-display))',
              fontSize: isMobile ? 22 : 19,
              fontWeight: 600,
              color: 'var(--brand-primary)',
            }}
          >
            {tip.item.es}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <LangTag code="PT" />
          <span style={{ fontSize: isMobile ? 17 : 15.5, color: 'var(--fg1)', fontWeight: 500 }}>
            {tip.item.pt}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: isMobile ? 14 : 13, lineHeight: 1.5, color: 'var(--fg3)' }}>
          {tip.item.tip}
        </p>
      </div>
      {closeBtn}
    </div>
  )

  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10,10,15,0.42)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            zIndex: 60,
          }}
        />
        <div
          ref={ref}
          role="dialog"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 61,
            background: 'var(--bg-card)',
            borderRadius: '20px 20px 0 0',
            borderTop: '1px solid var(--border)',
            padding: '22px 20px calc(22px + env(safe-area-inset-bottom, 0px))',
            boxShadow: '0 -10px 40px rgba(10,10,20,0.22)',
            animation: 'sheetUp .22s ease',
          }}
        >
          <div
            style={{
              width: 38,
              height: 4,
              borderRadius: 99,
              background: 'var(--border-strong)',
              margin: '0 auto 16px',
            }}
          />
          {inner}
        </div>
        <style>{`@keyframes sheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      </>
    )
  }

  // desktop popover ancorado abaixo da palavra (coords de viewport => position:fixed)
  const W = 300
  const left = Math.max(12, Math.min(tip.rect.left + tip.rect.width / 2 - W / 2, window.innerWidth - W - 12))
  return (
    <div
      ref={ref}
      role="dialog"
      style={{
        position: 'fixed',
        top: tip.rect.bottom + 10,
        left,
        width: W,
        zIndex: 61,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md, 14px)',
        padding: 16,
        boxShadow: 'var(--shadow-hover, 0 12px 32px rgba(20,20,40,0.16))',
        animation: 'tipIn .15s ease',
      }}
    >
      {inner}
      <style>{`@keyframes tipIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  )
}

/* ====================== COMPONENTE PRINCIPAL ====================== */
export default function SpanishReader({ contentPt, contentEs, vocab }: Props) {
  const [mode, setMode] = useState<Mode>('bilingual')
  const [tip, setTip] = useState<ActiveTip | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)')
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  const ptParas = getParagraphs(contentPt)
  const esParas = getParagraphs(contentEs)
  const count = Math.max(ptParas.length, esParas.length)

  const openTip = useCallback((item: VocabItem, el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    setTip((prev) =>
      prev?.item.es === item.es
        ? null
        : { item, rect: { left: r.left, top: r.top, bottom: r.bottom, width: r.width } }
    )
  }, [])

  const closeTip = useCallback(() => setTip(null), [])

  // modo parallel não faz sentido no mobile estreito -> renderiza como bilíngue
  const effectiveMode: Mode = isMobile && mode === 'parallel' ? 'bilingual' : mode

  const MODES: { id: Mode; label: string }[] = [
    { id: 'bilingual', label: 'Bilíngue' },
    { id: 'parallel', label: 'Lado a lado' },
    { id: 'portuguese', label: 'Só PT' },
    { id: 'spanish', label: 'Só ES' },
  ]
  const visibleModes = isMobile ? MODES.filter((m) => m.id !== 'parallel') : MODES

  /* ---- blocos de texto ---- */
  const ptBlock = (txt: string, key?: number) => (
    <div
      key={key}
      style={{
        borderLeft: '3px solid var(--border-strong)',
        background: 'var(--bg-alt)',
        borderRadius: '0 var(--r-sm, 10px) var(--r-sm, 10px) 0',
        padding: '12px 16px',
      }}
    >
      <div style={{ marginBottom: 6 }}>
        <LangTag code="PT" />
      </div>
      <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 15.5, lineHeight: 1.7, color: 'var(--fg2)' }}>
        {txt}
      </p>
    </div>
  )

  const esBlock = (txt: string, key?: number) => (
    <div
      key={key}
      style={{
        borderLeft: '3px solid var(--brand-primary)',
        background: 'color-mix(in oklab, var(--brand-primary) 5%, var(--bg-card))',
        borderRadius: '0 var(--r-sm, 10px) var(--r-sm, 10px) 0',
        padding: '12px 16px',
      }}
    >
      <div style={{ marginBottom: 6 }}>
        <LangTag code="ES" />
      </div>
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-editorial, var(--font-display))',
          fontSize: 17.5,
          lineHeight: 1.75,
          color: 'var(--fg1)',
        }}
      >
        <HighlightedText text={txt} vocab={vocab} activeEs={tip?.item.es ?? null} onTip={openTip} />
      </p>
    </div>
  )

  return (
    <div style={{ fontFamily: 'var(--font-body)' }}>
      {tip && <VocabTip tip={tip} isMobile={isMobile} onClose={closeTip} />}

      <section
        aria-label="Aprenda espanhol lendo esta história"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderTop: '3px solid var(--brand-primary)',
          borderRadius: 'var(--r-lg, 18px)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-soft)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px 16px', background: 'var(--bg-alt)', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div style={{ minWidth: 0 }}>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--fg1)',
                  lineHeight: 1.25,
                }}
              >
                Aprenda espanhol lendo esta história
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--fg3)', lineHeight: 1.4 }}>
                Toque nas palavras destacadas para ver a tradução e a dica
              </p>
            </div>
            <span
              style={{
                flexShrink: 0,
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                color: 'var(--brand-primary)',
                background: 'color-mix(in oklab, var(--brand-primary) 10%, transparent)',
                border: '1px solid color-mix(in oklab, var(--brand-primary) 20%, transparent)',
                padding: '4px 10px',
                borderRadius: 'var(--r-sm, 10px)',
                whiteSpace: 'nowrap',
              }}
            >
              A1 · Iniciante
            </span>
          </div>

          {/* Segmented control */}
          <div
            role="tablist"
            style={{
              display: 'flex',
              gap: 4,
              padding: 4,
              background: 'var(--bg-app)',
              borderRadius: 'var(--r-md, 14px)',
              border: '1px solid var(--border)',
            }}
          >
            {visibleModes.map((m) => {
              const active = effectiveMode === m.id
              return (
                <button
                  key={m.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(m.id)}
                  style={{
                    flex: 1,
                    minHeight: 42,
                    border: 'none',
                    borderRadius: 'var(--r-sm, 10px)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-display)',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: active ? 'var(--brand-primary)' : 'var(--fg2)',
                    background: active ? 'var(--bg-card)' : 'transparent',
                    boxShadow: active ? 'var(--shadow-soft)' : 'none',
                    transition: 'all .15s ease',
                  }}
                >
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Corpo */}
        {effectiveMode === 'parallel' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {ptParas.map((p, i) => ptBlock(p, i))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {esParas.map((p, i) => esBlock(p, i))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {Array.from({ length: count }, (_, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  padding: '16px 20px',
                  borderBottom: i < count - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                {(effectiveMode === 'bilingual' || effectiveMode === 'portuguese') && ptParas[i] && ptBlock(ptParas[i])}
                {effectiveMode !== 'portuguese' && esParas[i] && esBlock(esParas[i])}
              </div>
            ))}
          </div>
        )}

        {/* Vocabulário */}
        {vocab.length > 0 && (
          <div style={{ padding: '16px 20px', background: 'var(--bg-alt)', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <LangTag code="ES" />
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--fg3)',
                }}
              >
                Vocabulário da história
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {vocab.map((v, i) => {
                const active = tip?.item.es === v.es
                return (
                  <button
                    key={i}
                    onClick={(e) => openTip(v, e.currentTarget)}
                    style={{
                      minHeight: 36,
                      padding: '6px 13px',
                      borderRadius: 'var(--r-sm, 10px)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                      fontSize: 14,
                      fontWeight: active ? 700 : 500,
                      color: active ? 'var(--brand-primary)' : 'var(--fg1)',
                      background: active
                        ? 'color-mix(in oklab, var(--brand-primary) 12%, transparent)'
                        : 'var(--bg-card)',
                      border: active
                        ? '1px solid color-mix(in oklab, var(--brand-primary) 35%, transparent)'
                        : '1px solid var(--border)',
                      transition: 'all .15s ease',
                    }}
                  >
                    {v.es}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
